// Файл: src/components/ErrorBoundary.js
// Перехват ошибок рендера: вместо белой страницы — понятный экран
// с кнопками «Обновить» и «На главную». Ошибка уходит в reportClientError.
//
// resetKey: при его смене (например, при переходе на другую страницу)
// экран ошибки сбрасывается — упала одна страница, остальные работают.

import React from 'react';
import { Box, Button, Paper, Typography } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { withTranslation } from 'react-i18next';
import { reportClientError } from '../utils/errorReporting';

const CHUNK_RELOAD_KEY = 'chunk_reload_at';

// После деплоя открытая вкладка может запросить уже несуществующий кусок
// приложения (ChunkLoadError). Лечится перезагрузкой — делаем её сами,
// но не чаще раза в минуту, чтобы не уйти в цикл.
const isChunkError = (error) =>
  error?.name === 'ChunkLoadError' || /Loading (CSS )?chunk [\w-]+ failed/i.test(String(error?.message));

class ErrorBoundaryBase extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    if (isChunkError(error)) {
      try {
        const last = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY) || 0);
        if (Date.now() - last > 60 * 1000) {
          sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
          window.location.reload();
          return;
        }
      } catch {
        // sessionStorage недоступен — просто покажем экран ошибки
      }
    }
    reportClientError(error, { source: this.props.source || 'error-boundary', componentStack: info?.componentStack });
  }

  componentDidUpdate(prevProps) {
    if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  render() {
    const { error } = this.state;
    const { t, children, fullScreen } = this.props;
    if (!error) return children;

    return (
      <Box
        sx={{
          minHeight: fullScreen ? '100vh' : '60vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 3,
          bgcolor: fullScreen ? 'background.default' : 'transparent',
        }}
      >
        <Paper
          elevation={0}
          sx={{ p: 4, maxWidth: 460, textAlign: 'center', borderRadius: 3, border: (th) => `1px solid ${th.palette.divider}` }}
        >
          <ErrorOutlineIcon sx={{ fontSize: 48, color: 'error.main', mb: 1 }} />
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
            {t('error_boundary.title', 'Что-то пошло не так')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {t('error_boundary.text', 'На этой странице произошла ошибка. Мы уже получили о ней сообщение. Попробуйте обновить страницу.')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button variant="contained" onClick={() => window.location.reload()} sx={{ textTransform: 'none', borderRadius: 2 }}>
              {t('error_boundary.reload', 'Обновить страницу')}
            </Button>
            <Button variant="outlined" onClick={() => { window.location.href = '/dashboard'; }} sx={{ textTransform: 'none', borderRadius: 2 }}>
              {t('error_boundary.home', 'На главную')}
            </Button>
          </Box>
          <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 2 }}>
            {new Date().toLocaleString()} · {String(error?.message || '').slice(0, 120)}
          </Typography>
        </Paper>
      </Box>
    );
  }
}

const ErrorBoundary = withTranslation()(ErrorBoundaryBase);
export default ErrorBoundary;
